'use client';

import React, { useEffect, useState } from 'react';
import { useAOS } from '@/hooks/useAOS';
import Image from 'next/image';
import { getImageUrl } from '@/lib/imageUrl';

/* -------------------- TYPES -------------------- */

type FeatureCardProps = {
    title: string;
    description: string;
    imageMobile: string;
    imageDesktop: string;
    isMobile: boolean;
};

type AccordionItemProps = {
    title: string;
    open?: boolean;
};

/* -------------------- HOOK -------------------- */

const useIsMobile = (breakpoint = 768) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < breakpoint);
        check(); // run on mount
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, [breakpoint]);

    return isMobile;
};

/* -------------------- COMPONENTS -------------------- */

const FeatureCard = ({ title, description, imageMobile, imageDesktop, isMobile }: FeatureCardProps) => {
    return (
        <div className="bg-[#EAFFE0] rounded-2xl p-6 sm:p-8 text-center hover:shadow-lg transition h-full">
            <h3 className="font-semibold text-[24px] font-NeueMontreal text-[#000] whitespace-wrap">
                {title}
            </h3>
            <p className="mt-3 text-gray-600 text-[16px] font-Schibsted_Grotesk">
                {description}
            </p>
            <Image
                src={isMobile ? imageMobile : imageDesktop}
                alt={title}
                width={256}
                height={256}
                className="w-40 sm:w-52 md:w-56 mx-auto mt-4"
                priority
            />
        </div>
    );
};

const AccordionItem = ({ title, open = false }: AccordionItemProps) => {
    const [isOpen, setIsOpen] = useState(open);

    return (
        <div className="border-b border-green-200 py-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="font-NeueMontreal w-full flex justify-between items-center text-left font-medium text-[#003114] hover:text-[#0EAC0E] text-[20px] sm:text-base"
            >
                {title}
                <span className="text-xl">{isOpen ? '−' : '+'}</span>
            </button>

            {isOpen && (
                <p className="font-Schibsted_Grotesk mt-3 text-[16px] sm:text-sm text-[#2D3238]">
                    Show products or services directly in WhatsApp chats. Customers browse,
                    choose, and enquire instantly.
                </p>
            )}
        </div>
    );
};

/* -------------------- MAIN SECTION -------------------- */

const TurnWhatsapp = () => {
    const isMobile = useIsMobile(); // 👈 single source of truth

    useAOS();

    return (
        <main
            data-aos="fade-up"
            data-aos-duration="1000"
            className="bg-[#F4FFE9] min-h-screen pt-[24px] pb-[60px] md:pt-[40px] md:pb-[120px]"
        >
            {/* Hero Section */}
            <section className="max-w-7xl mx-auto text-center">
                <h2 className="text-[36px] md:text-[48px] font-bold text-[#003114] font-NeueMontreal md:max-w-7xl">
                    Turn WhatsApp Conversations into Sales
                </h2>
                <p className="mt-4 text-gray-600 !max-w-[800px] mx-auto font-Schibsted_Grotesk">
                    Chatbot helps businesses engage customers, capture orders, and collect
                    payments directly inside WhatsApp using smart automation.
                </p>
            </section>

            {/* Feature Cards */}
            <section className="max-w-7xl mx-auto w-full mt-10 sm:mt-16 border-b border-black/10 pb-16 px-4 sm:px-6 lg:px-8 grid gap-6 sm:gap-8 md:grid-cols-3">
                <FeatureCard
                    title="Smart Customer Conversations"
                    description="Automated replies using trigger words, buttons, and menus guide customers."
                    imageMobile={getImageUrl("/vilvabusiness/website/products/vilvachat/sm_1mob_1.webp")}
                    imageDesktop={getImageUrl("/vilvabusiness/website/products/vilvachat/sm-1.webp")}
                    isMobile={isMobile}
                />
                <FeatureCard
                    title="Orders, Payments & Data"
                    description="Customers can browse products, place orders, and make payments without leaving WhatsApp."
                    imageMobile={getImageUrl("/vilvabusiness/website/products/vilvachat/sm_2mob_1.webp")}
                    imageDesktop={getImageUrl("/vilvabusiness/website/products/vilvachat/sm-2.webp")}
                    isMobile={isMobile}
                />
                <FeatureCard
                    title="End-to-End Workflow Automation"
                    description="From first message to order confirmation, everything runs automatically."
                    imageMobile={getImageUrl("/vilvabusiness/website/products/vilvachat/sm_3mob_1.webp")}
                    imageDesktop={getImageUrl("/vilvabusiness/website/products/vilvachat/sm-3.webp")}
                    isMobile={isMobile}
                />
            </section>

            {/* Split Section */}
            <section className="max-w-7xl mx-auto mt-[60px] md:mt-[59px] flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
                {/* Left Visual */}
                <div className="rounded-3xl p-4 sm:p-10 flex justify-center w-full">
                    <div className="relative w-full">
                        <Image
                            src={
                                isMobile
                                    ? getImageUrl("/vilvabusiness/website/products/vilvachat/sm_4mob_1.webp")
                                    : getImageUrl("/vilvabusiness/website/products/vilvachat/sm-4.webp")
                            }
                            alt="Vilva Chat WhatsApp automation and broadcast tools"
                            width={isMobile ? 600 : 1200}
                            height={isMobile ? 600 : 675}
                            className="w-full h-auto object-contain"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>

                {/* Right Accordion */}
                <div className="w-full max-w-xl md:px-0 px-[16px]">
                    <AccordionItem title="WhatsApp Product Catalog" open />
                    <AccordionItem title="Automated Messages & Buttons" />
                    <AccordionItem title="Address Collection & Data Sync" />
                    <AccordionItem title="WhatsApp Payment Integration" />
                    <AccordionItem title="Security & Reliability" />
                    <AccordionItem title="Suitable for Every Industry" />
                </div>
            </section>
        </main>
    );
};

export default TurnWhatsapp;